/**
 * Cookiebot auto-blocking (`uc.js`, `data-blockingmode=auto`) replays queued
 * listeners with `dispatchEvent`. Those events are untrusted.
 *
 * An early replay hits an in-flight XHR whose `status` is still 0. Angular then
 * reports `HttpErrorResponse` "0 OK" (`statusText` falls back to "OK" when empty).
 * The same mechanism also replays the real completion after the browser has a
 * status. That replay is the event Angular must handle, because Cookiebot does
 * not deliver the original trusted `load` to the wrapped listener.
 */
const COMPLETION_EVENT_TYPES = new Set([
  'load',
  'error',
  'timeout',
  'abort',
  'progress',
]);

export function isUntrustedXhrCompletionEvent(
  type: string,
  event: Event,
): boolean {
  return COMPLETION_EVENT_TYPES.has(type) && event.isTrusted === false;
}

export function shouldIgnoreUntrustedXhrCompletion(
  type: string,
  event: Event,
  target: EventTarget,
): boolean {
  if (!isUntrustedXhrCompletionEvent(type, event)) {
    return false;
  }

  const xhr = target as Partial<XMLHttpRequest>;
  if (typeof xhr.readyState !== 'number' || typeof xhr.status !== 'number') {
    return true;
  }

  return xhr.readyState !== 4 || xhr.status === 0;
}

interface ListenerTarget {
  addEventListener: EventTarget['addEventListener'];
  removeEventListener: EventTarget['removeEventListener'];
}

export function ignoreUntrustedXhrCompletionEvents<T extends ListenerTarget>(
  target: T,
): T {
  const originalAdd = target.addEventListener.bind(target);
  const originalRemove = target.removeEventListener.bind(target);
  const wrappers = new WeakMap<object, Map<string, EventListener>>();

  const wrapperFor = (
    type: string,
    listener: EventListenerOrEventListenerObject,
  ): EventListener => {
    let byType = wrappers.get(listener);
    if (!byType) {
      byType = new Map();
      wrappers.set(listener, byType);
    }
    let wrapped = byType.get(type);
    if (!wrapped) {
      wrapped = function (this: EventTarget, event: Event) {
        if (shouldIgnoreUntrustedXhrCompletion(type, event, this)) {
          return;
        }
        if (typeof listener === 'function') {
          listener.call(this, event);
        } else {
          listener.handleEvent(event);
        }
      };
      byType.set(type, wrapped);
    }
    return wrapped;
  };

  target.addEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ) => {
    if (listener == null || !COMPLETION_EVENT_TYPES.has(type)) {
      return originalAdd(type, listener as EventListener, options);
    }
    return originalAdd(type, wrapperFor(type, listener), options);
  }) as EventTarget['addEventListener'];

  target.removeEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions,
  ) => {
    if (listener == null) {
      return originalRemove(type, listener as EventListener, options);
    }
    const wrapped = wrappers.get(listener)?.get(type);
    return originalRemove(
      type,
      wrapped ?? (listener as EventListener),
      options,
    );
  }) as EventTarget['removeEventListener'];

  return target;
}
